import { CategoryType, ChartSpecificColumnType, ColumnAggregationType, ColumnTimeBucket, ColumnType, CurrencyFormatType, DataType, FormatType, NegativeValueFormat, SettingsElementType, Unit, } from '@thoughtspot/ts-chart-sdk';
import Joi from 'joi';
import { BackgroundFormatTypes, ConditionalFormattingComparisonTypes, Operators, } from '../../types/prism';
const TranslatedValue = Joi.object({
    valueTranslation: Joi.string(),
    value: Joi.string(),
});
const FontPropertiesSchema = Joi.object({
    __typename: Joi.string().valid('FontAttrs'),
    color: Joi.string().allow(null),
    bold: Joi.boolean().allow(null),
    italic: Joi.boolean().allow(null),
    strikeThrough: Joi.boolean().allow(null),
    underline: Joi.boolean().allow(null),
});
const ConditionalFormatConfigSchema = Joi.object({
    __typename: Joi.string().valid('ConditionalMetric'),
    operator: Joi.string()
        .valid(...Object.values(Operators))
        .allow(null),
    rangeValues: Joi.object({
        __typename: Joi.string().valid('Range'),
        min: Joi.number().unsafe(),
        max: Joi.number().unsafe(),
    }).allow(null),
    value: Joi.string().allow(null, ''),
    plotAsBand: Joi.boolean().allow(null),
    fontProperties: FontPropertiesSchema.allow(null),
    backgroundFormatType: Joi.string()
        .valid(...Object.values(BackgroundFormatTypes))
        .allow(null),
    gradientBackgroundAttrs: Joi.object({
        __typename: Joi.string().valid('GradientBackgroundAttrs'),
        backgroundFormatRange: Joi.array()
            .items(Joi.number())
            .allow(null),
        backgroundFormatMidpoint: Joi.number()
            .unsafe()
            .allow(null),
        colors: Joi.array()
            .items(Joi.string().allow(null, ''))
            .allow(null, ''),
        isAutoScaled: Joi.boolean().allow(null),
    }).allow(null),
    solidBackgroundAttrs: Joi.object({
        __typename: Joi.string().valid('SolidBackgroundAttrs'),
        color: Joi.string().allow(null),
    }).allow(null),
    comparisonType: Joi.string()
        .valid(...Object.values(ConditionalFormattingComparisonTypes))
        .allow(null),
    lhsColumnId: Joi.string().allow(null),
    rhsColumnId: Joi.string().allow(null),
    comparisonParameterId: Joi.string().allow(null),
    isHighlightRow: Joi.boolean().allow(null),
});
const numberFormatConfigSchema = Joi.object({
    __typename: Joi.string().valid('FormatConfig'),
    category: Joi.string().valid(...Object.values(CategoryType)),
    numberFormatConfig: Joi.object({
        __typename: Joi.string().valid('NumberFormatConfig'),
        unit: Joi.string().valid(...Object.values(Unit)),
        decimals: Joi.number(),
        negativeValueFormat: Joi.string().valid(...Object.values(NegativeValueFormat)),
        toSeparateThousands: Joi.boolean(),
        removeTrailingZeroes: Joi.boolean().allow(null),
    }).allow(null),
    percentageFormatConfig: Joi.object({
        __typename: Joi.string().valid('PercentageFormatConfig'),
        decimals: Joi.number(),
        removeTrailingZeroes: Joi.boolean().allow(null),
    }).allow(null),
    currencyFormatConfig: Joi.object({
        __typename: Joi.string().valid('CurrencyFormatConfig'),
        locale: Joi.string(),
        unit: Joi.string().valid(...Object.values(Unit)),
        decimals: Joi.number(),
        toSeparateThousands: Joi.boolean(),
        removeTrailingZeroes: Joi.boolean().allow(null),
    }).allow(null),
    customFormatConfig: Joi.object({
        __typename: Joi.string().allow('CustomFormatConfig'),
        format: Joi.string().allow(null),
    }).allow(null),
    isCategoryEditable: Joi.boolean().allow(null),
});
const ColumnFormatSchema = Joi.object({
    pattern: Joi.string().allow(null, ''),
    currencyFormat: Joi.object({
        type: Joi.string()
            .valid(...Object.values(CurrencyFormatType))
            .required(),
        column: Joi.string()
            .allow('')
            .required(),
        isoCode: Joi.string()
            .allow('')
            .required(),
    }).allow(null),
    type: Joi.string()
        .valid(...Object.values(FormatType))
        .required(),
}).unknown(false);
const ChartColumnSchema = Joi.object({
    id: Joi.string().required(),
    name: Joi.string().required(),
    type: Joi.string()
        .valid(...Object.values(ColumnType))
        .required(),
    chartSpecificColumnType: Joi.string()
        .valid(...Object.values(ChartSpecificColumnType))
        .required(),
    timeBucket: Joi.string()
        .valid(...Object.values(ColumnTimeBucket))
        .allow(null)
        .required(),
    dataType: Joi.string()
        .valid(...Object.values(DataType))
        .required(),
    format: ColumnFormatSchema.allow(null),
    customOrder: Joi.array()
        .items(Joi.string())
        .required(),
    aggregationType: Joi.string()
        .valid(...Object.values(ColumnAggregationType))
        .required(),
    calenderGuid: Joi.string().allow(null, ''),
    columnProperties: Joi.object({
        conditionalFormatting: Joi.object({
            __typename: Joi.string().valid('ConditionalFormatting'),
            rows: Joi.array().items(ConditionalFormatConfigSchema),
        }).allow(null),
        numberFormatting: numberFormatConfigSchema.allow(null),
    }).unknown(false),
    displayName: Joi.string().allow(null, ''),
    dynamicTitle: Joi.string().allow(null, ''),
    showGrowth: Joi.boolean().allow(null),
    isAggregateApplied: Joi.boolean().allow(null),
}).unknown(false);
const QueryColumnsSchema = ChartColumnSchema.append({
    isMeasureValue: Joi.boolean(),
});
const QueryParamsSchema = Joi.object({
    offset: Joi.number(),
    size: Joi.number(),
}).unknown(false);
const QuerySchema = Joi.object({
    queryColumns: Joi.array()
        .items(QueryColumnsSchema)
        .min(1)
        .required(),
    queryParams: QueryParamsSchema.allow(null),
}).unknown(false);
const settingElementSchema = Joi.object({
    key: Joi.string().optional(),
    elementType: Joi.string()
        .valid(...Object.values(SettingsElementType))
        .required(),
    children: Joi.array()
        .items(Joi.link('#settingElement'))
        .optional(),
    properties: Joi.object()
        .pattern(Joi.string(), Joi.alternatives().try(Joi.string().allow(''), Joi.boolean(), Joi.number(), Joi.object(), Joi.array()))
        .optional(),
    className: Joi.string().optional(),
    isDisable: Joi.boolean().optional(),
    canHaveAutoValue: Joi.boolean().optional(),
    runTimeProperties: Joi.any(),
    isAdvanced: Joi.boolean().optional(),
    isAnswerProperty: Joi.boolean().optional(),
    helperText: Joi.string().optional(),
    link: Joi.object({
        elementId: Joi.string().required(),
        isLinked: Joi.boolean().required(),
    }).optional(),
    translate: Joi.array()
        .items(Joi.string())
        .optional(),
}).id('settingElement');
const colorPickerFormDetailSchema = Joi.object({
    type: Joi.string()
        .valid('colorpicker')
        .required(),
    key: Joi.alternatives()
        .try(Joi.string(), Joi.number())
        .required(),
    label: Joi.alternatives().try(Joi.string(), Joi.number()),
    selectorType: Joi.string().valid('FONT', 'COLOR'),
    defaultValue: Joi.string(),
    labelTranslation: Joi.string().allow(null),
}).label('ColorPickerFormDetail');
const toggleFormDetailSchema = Joi.object({
    type: Joi.string()
        .valid('toggle')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    defaultValue: Joi.boolean(),
    disabled: Joi.boolean().allow(null),
    labelTranslation: Joi.string().allow(null),
}).label('ToggleFormDetail');
const checkboxFormDetailSchema = Joi.object({
    type: Joi.string()
        .valid('checkbox')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    defaultValue: Joi.boolean(),
    disabled: Joi.boolean().allow(null),
    labelTranslation: Joi.string().allow(null),
}).label('CheckboxFormDetail');
const radioButtonFormDetailSchema = Joi.object({
    type: Joi.string()
        .valid('radio')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    defaultValue: Joi.alternatives().try(Joi.string(), TranslatedValue),
    values: Joi.alternatives()
        .try(Joi.array().items(Joi.string()), Joi.array().items(TranslatedValue))
        .required(),
    disabled: Joi.boolean().allow(null),
    labelTranslation: Joi.string().allow(null),
}).label('RadioButtonFormDetail');
const dropDownFormDetailSchema = Joi.object({
    type: Joi.string()
        .valid('dropdown')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    defaultValue: Joi.alternatives().try(Joi.string(), TranslatedValue),
    values: Joi.alternatives()
        .try(Joi.array().items(Joi.string()), Joi.array().items(TranslatedValue))
        .required(),
    disabled: Joi.boolean().allow(null),
    labelTranslation: Joi.string().allow(null),
}).label('DropDownFormDetail');
const inputValidationSchema = Joi.object({
    required: Joi.boolean(),
    requiredError: Joi.string(),
    regex: Joi.string().regex(/^\/.*\/[gimuy]*$/),
    regexError: Joi.string(),
    minLength: Joi.number()
        .integer()
        .min(0),
    minLengthError: Joi.string(),
    range: Joi.string(),
    rangeError: Joi.string(),
}).label('InputValidation');
const textInputFormDetailSchema = Joi.object({
    type: Joi.string()
        .valid('text')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    password: Joi.boolean(),
    multiline: Joi.boolean(),
    placeholder: Joi.string(),
    defaultValue: Joi.string(),
    inputValidation: inputValidationSchema,
    disabled: Joi.boolean().allow(null),
    labelTranslation: Joi.string().allow(null),
}).label('TextInputFormDetail');
const numberInputFormDetailSchema = Joi.object({
    type: Joi.string()
        .valid('number')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    defaultValue: Joi.number(),
    inputValidation: inputValidationSchema,
    disabled: Joi.boolean().allow(null),
    labelTranslation: Joi.string().allow(null),
}).label('NumberInputFormDetail');
const tooltipFormSchema = Joi.object({
    type: Joi.string()
        .valid('tooltipconfig')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    defaultValue: Joi.object({
        columnIds: Joi.array().items(Joi.string()),
    }).allow(null),
    disabled: Joi.boolean().allow(null),
    labelTranslation: Joi.string().allow(null),
}).label('ToolTipInputFormDetail');
const customsortSchema = Joi.object({
    type: Joi.string()
        .valid('customsort')
        .required(),
    key: Joi.string().required(),
    columnId: Joi.string().required(),
    orientation: Joi.string().allow(null),
}).label('CustomSortFormDetail');
const sectionSchema = Joi.object({
    type: Joi.string()
        .valid('section')
        .required(),
    key: Joi.string().required(),
    label: Joi.string(),
    disabled: Joi.boolean().allow(null),
    children: Joi.array()
        .items(Joi.link('#VisualPropElement'))
        .default([]),
    alignment: Joi.string().valid('row', 'column'),
    layoutType: Joi.string().valid('accordion', 'tab', 'none'),
    isAccordianExpanded: Joi.boolean(),
    labelTranslation: Joi.string().allow(null),
}).label('Section');
const visualPropElementSchema = Joi.alternatives()
    .try(textInputFormDetailSchema, numberInputFormDetailSchema, colorPickerFormDetailSchema, toggleFormDetailSchema, checkboxFormDetailSchema, radioButtonFormDetailSchema, dropDownFormDetailSchema, sectionSchema, tooltipFormSchema, customsortSchema)
    .id('VisualPropElement');
const columnPropSchemas = Joi.object({
    type: Joi.string()
        .valid(...Object.values(ColumnType))
        .required(),
    columnSettingsDefinition: Joi.object().pattern(Joi.string(), Joi.object({
        elements: Joi.alternatives().try(Joi.array().items(visualPropElementSchema), Joi.array().items(settingElementSchema)),
    })),
});
const visualPropEditorDefinitionSchema = Joi.object({
    elements: Joi.array().items(visualPropElementSchema),
    columnsVizPropDefinition: Joi.array().items(columnPropSchemas),
    axisVizPropDefinition: Joi.array().items(settingElementSchema),
    dataLabelVizPropDefinition: Joi.array().items(settingElementSchema),
    tooltipVizPropDefinition: Joi.array().items(settingElementSchema),
    legendVizPropDefinition: Joi.array().items(settingElementSchema),
    displayVizPropDefinition: Joi.alternatives().try(Joi.array().items(visualPropElementSchema), Joi.array().items(settingElementSchema)),
});
const sdkChartConfigSectionSchema = Joi.object({
    key: Joi.string().required(),
    label: Joi.string().allow(''),
    labelTranslation: Joi.string().allow(null),
    maxColumnCount: Joi.number(),
    allowMeasureColumns: Joi.boolean(),
    allowAttributeColumns: Joi.boolean(),
    allowTimeSeriesColumns: Joi.boolean(),
    allowMeasureNameColumn: Joi.boolean(),
    allowMeasureValueColumn: Joi.boolean(),
    allowValuesOnlyInLastPosition: Joi.boolean(),
    allowFlatItems: Joi.boolean(),
    allowMergedItems: Joi.boolean(),
    allowDualItems: Joi.boolean(),
    minMergeItems: Joi.number(),
    minDualItems: Joi.number(),
    maxMergeItems: Joi.number(),
    maxDualItems: Joi.number(),
}).unknown(false);
const sdkChartConfigEditorDefinitionSchema = Joi.object({
    key: Joi.string().required(),
    label: Joi.string().allow(''),
    descriptionText: Joi.string().allow(''),
    columnSections: Joi.array()
        .items(sdkChartConfigSectionSchema)
        .min(1)
        .required(),
}).unknown(false);
export { QuerySchema, ChartColumnSchema, visualPropEditorDefinitionSchema, sdkChartConfigEditorDefinitionSchema, settingElementSchema, };
//# sourceMappingURL=joi-validation-schemas.js.map